import React, { useEffect, useMemo, useState } from "react";
import { Divider, Box } from "@mantine/core";


import { useLocation, useParams } from "react-router-dom";
import PreviewViewport from "../components/PreviewViewport";
import ThirdPersonPreviewViewport from "../components/ThirdPersonPreviewViewport";
import { useObjectStore } from "../zustand/objects";
import { useAccountStore } from "../zustand/account";

import SwitchToPreviewButton from "../components/SwitchToPreviewButton";
import { AddCommentButton } from "../components/AddCommentButton";
import SwitchVRMode from "../components/SwitchVRMode";
import StoryboardLinkButton from "../components/StoryboardLinkButton";
import ChangePreviewArtboard from "../components/ChangePreviewArtboard";
import { postRealtimeToken } from '../api.js';
import {
  debouncedRealtimeEventUpdate,
  cancelEditorRealtimeDebouncers,
  isPresenceUpdateType,
} from "../utils/realtimeDebounce";
import PreviewChrome from "../components/PreviewChrome";
import { MBX_DARK_BACKGROUND_FILL } from "../styleFile";

function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

const PreviewPage = ({ match }) => {
  const [realtime, setRealtime] = useState(false);

  let query = useQuery();

  const queryString = query.get("artboard")

  const {
    currentObjectArtboard,
    presence,
    uniqueRealtimeId,
    xanoClient,
    updateArtboard,
    getSpecificUserProject,
    unloadObjects,
    loadObjects,
    previewVR,
    objectsAreLoaded,
    floorColour,
    artboards,
    getAIModels,
    viewportThemeOverride,
  } = useObjectStore();
  const isDarkTopBar = viewportThemeOverride === "dark";
  const backgroundFill = isDarkTopBar ? MBX_DARK_BACKGROUND_FILL : "#FFFFFF";

  const lastProjectSaveAt = useObjectStore((s) => s.lastProjectSaveAt);
  const lastProjectRemoteUpdateAt = useObjectStore((s) => s.lastProjectRemoteUpdateAt);

  const { projectKey } = useParams();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // These store actions aren't guaranteed to return Promises, so avoid `.then(...)` chains.
      await Promise.resolve(unloadObjects());
      if (cancelled) return;

      await Promise.resolve(getSpecificUserProject(projectKey));
      if (cancelled) return;

      await Promise.resolve(getAIModels());
      if (cancelled) return;

      await Promise.resolve(loadObjects());
      if (cancelled) return;

      await Promise.resolve(queryString ? updateArtboard(queryString) : updateArtboard("1"));
    })();

    return () => {
      cancelled = true;
    };
  }, [
    getAIModels,
    getSpecificUserProject,
    loadObjects,
    projectKey,
    queryString,
    unloadObjects,
    updateArtboard,
  ]);

  // Xano realtime: must run in useEffect — creating the channel during render re-ran on every
  // React commit, spawned duplicate sockets, and called send() while still CONNECTING.
  useEffect(() => {
    if (!projectKey) return undefined;

    let projectChannel;
    try {
      projectChannel = xanoClient.channel(`project/${projectKey}`, { presence: true });
      if (!projectChannel || typeof projectChannel.on !== "function") {
        return undefined;
      }

      projectChannel.on("connection_status", (action) => {
        if (action.payload.status === "connected") {
          setRealtime(true);
          xanoClient.setRealtimeAuthToken(localStorage.getItem("user"));
          postRealtimeToken().then((res) => {
            useAccountStore.getState().updateFirstName(res.firstName);
            useObjectStore.getState().setRealtimeToken(res.id);
          });
        } else if (action.payload.status === "disconnected") {
          setRealtime(false);
        }
      });

      projectChannel.on("presence_update", (action) => {
        const st = useObjectStore.getState();
        if (action.payload.action === "join") {
          st.addOrUpdatePresence(action.payload.presence.socketId, "editor", action);
        } else if (action.payload.action === "leave") {
          st.removePresence(action.payload.presence.socketId);
        }
      });

      projectChannel.on("message", (action) => {
        const st = useObjectStore.getState();
        if (
          action.options?.type != null &&
          action.options.type !== "updateFunction"
        ) {
          return;
        }

        const p = action.payload;

        if (isPresenceUpdateType(p.updateType)) {
          if (p.function === "bringToMe") {
            const ab = p.artboard;
            if (ab != null && ab !== "") {
              st.updateArtboardAndPreviewCamera(String(ab));
            }
            return;
          }

          const field = p.field;
          const chunk = field && p.update?.[field];
          const socketId = action.client?.socketId;
          if (field != null && chunk != null && socketId != null) {
            st.recordRealtimeClientSocketFromPresenceAction(action);
            st.addOrUpdatePresence(socketId, chunk.page, action);
          }
          return;
        }

        // Do not gate on realtime_token (per-user). Same account may have editor + preview open;
        // echoes would match token and were dropped here. EmbedSharePreviewPage already applies all.
        debouncedRealtimeEventUpdate(action);
      });
    } catch (err) {
      console.log("Error setting up Xano channel:", err);
    }

    return () => {
      cancelEditorRealtimeDebouncers();
      useObjectStore.getState().setRealtimeClientSocketId(null);
      if (projectChannel && typeof projectChannel.destroy === "function") {
        projectChannel.destroy();
      }
    };
  }, [projectKey, xanoClient]);

  useEffect(() => {
    if (!projectKey) return undefined;
    const id = window.setInterval(() => {
      useObjectStore.getState().pruneStalePresence(28000);
    }, 4000);
    return () => window.clearInterval(id);
  }, [projectKey]);

  const iconColour = {
    normal: isDarkTopBar ? "#E0E0E0" : "#747B82",
    hover: isDarkTopBar ? "#E0E0E0" : "#111827",
    border: isDarkTopBar ? "#4B5563" : "#E5E7EB",
    hoverBg: isDarkTopBar ? "#2f2f2f" : "#F9FAFB",
    disabledBg: isDarkTopBar ? "#2f2f2f" : "#E9ECEF",
    disabledIcon: isDarkTopBar ? "#9CA3AF" : "#C1C2C5",
  };

  const toolbarTrailing = previewVR ? (
    <>
      <StoryboardLinkButton projectKey={projectKey} iconColour={iconColour} />
      <SwitchVRMode iconColour={iconColour} />
      <SwitchToPreviewButton mode="preview" projectKey={projectKey} iconColour={iconColour} />
    </>
  ) : (
    <>
      <StoryboardLinkButton projectKey={projectKey} iconColour={iconColour} />
      <Divider orientation="vertical" style={{ height: 20 }} />
      <SwitchVRMode iconColour={iconColour} />
      <Divider orientation="vertical" style={{ height: 20 }} />
      <SwitchToPreviewButton mode="preview" projectKey={projectKey} iconColour={iconColour} />
      <Divider orientation="vertical" style={{ height: 20 }} />
      <AddCommentButton />
    </>
  );

  return (
    <>
      <Box
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: backgroundFill,
          zIndex: 0,
        }}
      />
      <PreviewChrome
        connected={realtime}
        lastSaveAt={lastProjectSaveAt}
        lastRemoteUpdateAt={lastProjectRemoteUpdateAt}
        toolbarLeading={!previewVR ? <ChangePreviewArtboard /> : null}
        toolbarTrailing={toolbarTrailing}
        isDarkTopBar={isDarkTopBar}
      />
      {previewVR ? (
        objectsAreLoaded &&
        artboards &&
        currentObjectArtboard && (
          <PreviewViewport
            presence={presence}
            uniqueRealtimeId={uniqueRealtimeId}
            floorColour={floorColour}
            artboards={artboards}
            projectKey={projectKey}
          />
        )
      ) : (
        objectsAreLoaded &&
        artboards &&
        currentObjectArtboard && (
          <ThirdPersonPreviewViewport
            presence={presence}
            uniqueRealtimeId={uniqueRealtimeId}
            floorColour={floorColour}
            artboards={artboards}
            projectKey={projectKey}
          />
        )
      )}
    </>
  );
};

export default PreviewPage;
